import { theme } from "@/config/project";
import { Box, Link } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { parsePageReferences } from "./utils/parsePageReferences";

interface IClickablePageReferencesProps {
    pageString: string;
    pageNavigationPluginInstance: PageNavigationPlugin;
}

/**
 * Component that renders clickable page references
 */
export const ClickablePageReferences = ({
    pageString,
    pageNavigationPluginInstance,
}: IClickablePageReferencesProps) => {
    const pages = parsePageReferences(pageString);
    const { jumpToPage } = pageNavigationPluginInstance;

    const handlePageClick = (pageNum: number) => {
        // PDF viewer uses 0-indexed pages, but display is 1-indexed
        jumpToPage(pageNum - 1);
    };

    // Group consecutive pages into ranges for display
    const formatPageRanges = (pageNumbers: number[]): string[] => {
        if (pageNumbers.length === 0) return [];

        const sortedPages = [...pageNumbers].sort((a, b) => a - b);
        const ranges: string[] = [];
        let rangeStart = sortedPages[0];
        let rangeEnd = sortedPages[0];

        for (let i = 1; i < sortedPages.length; i++) {
            if (sortedPages[i] === rangeEnd + 1) {
                rangeEnd = sortedPages[i];
            } else {
                ranges.push(
                    rangeStart === rangeEnd
                        ? `${rangeStart}`
                        : `${rangeStart}-${rangeEnd}`
                );
                rangeStart = sortedPages[i];
                rangeEnd = sortedPages[i];
            }
        }

        ranges.push(
            rangeStart === rangeEnd
                ? `${rangeStart}`
                : `${rangeStart}-${rangeEnd}`
        );

        return ranges;
    };

    const pageRanges = formatPageRanges(pages);

    return (
        <Box component="span">
            References pages:{" "}
            {pageRanges.map((range, index) => {
                const isRange = range.includes("-");
                const [start, end] = isRange
                    ? range.split("-").map(Number)
                    : [Number(range), Number(range)];

                if (isRange) {
                    // For ranges, make both the start and end clickable
                    return (
                        <Box component="span" key={index}>
                            {index > 0 && ", "}
                            <Link
                                component="button"
                                onClick={() => handlePageClick(start)}
                                sx={{
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                {start}
                            </Link>
                            -
                            <Link
                                component="button"
                                onClick={() => handlePageClick(end)}
                                sx={{
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                {end}
                            </Link>
                        </Box>
                    );
                } else {
                    // For single pages
                    return (
                        <Box component="span" key={index}>
                            {index > 0 && ", "}
                            <Link
                                component="button"
                                onClick={() => handlePageClick(start)}
                                sx={{
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                {range}
                            </Link>
                        </Box>
                    );
                }
            })}
        </Box>
    );
};
